import { readRefusal } from "../api/apiError";
import { useAuthStore } from "./store";

// HTTP-обёртка для авторизованных запросов.
//
// После #916 session-JWT хранится в HttpOnly cookie `baton_session`
// (JS его не видит). Поэтому обёртка БОЛЬШЕ НЕ ставит
// `Authorization: Bearer` из JS — вместо этого включает
// `credentials: "include"`, и браузер сам прикладывает cookie к
// same-origin запросу. На 401 ветвит реакцию по коду отказа: `not_authenticated` (и 401 без
// объектного `detail`) чистит identity-флаг в store, `org_context_required`
// сбрасывает активную организацию, иной код сессию не трогает (UI-логика
// редиректа — в `<ProtectedRoute>`: следующий рендер увидит
// `identity === null` и сделает Navigate). На 403 identity-флаг НЕ
// чистится: 403 значит «аутентифицирован, но операция запрещена»
// (например, `switchOrganization` на отозванное членство) — сессия
// жива, разлогинивать пользователя не за что.
//
// TanStack Query не предоставляет «глобальный fetch interceptor»
// (axios-style). Поэтому `features/*/api.ts` зовут эту функцию
// вместо голого `fetch`.

// Принимаем те же опции, что и `fetch`. Bun/Node-only Init расширения
// нам не нужны.
export interface AuthedFetchInit extends RequestInit {
  /**
   * Если запрос вернёт 401 — не очищать store. Используется в
   * самих auth-endpoint'ах (`POST /login` 401 ≠ «сессия протухла»,
   * а «пароль неверный» — это нормальная ситуация формы логина,
   * выкидывать user'а на login страницу не нужно — он уже на ней).
   */
  readonly skipAutoLogout?: boolean;
}

// HTTP-код «не аутентифицирован» — выносим в константу, чтобы тесты
// могли импортнуть и не дублировать magic number.
export const HTTP_UNAUTHORIZED = 401;

// Коды тела отказа `401`, по которым клиент ветвит реакцию на сессию.
export const REFUSAL_NOT_AUTHENTICATED = "not_authenticated";
export const REFUSAL_ORG_CONTEXT_REQUIRED = "org_context_required";

export async function authedFetch(
  input: RequestInfo | URL,
  init: AuthedFetchInit = {},
): Promise<Response> {
  const { skipAutoLogout, ...rest } = init;

  // `credentials: "include"` — браузер прикладывает HttpOnly cookie
  // `baton_session` к запросу. Без этого same-origin fetch шлёт
  // cookie по умолчанию, но включаем явно — устойчиво к смене
  // origin'а в dev-proxy и читаемо в коде.
  const response = await fetch(input, { ...rest, credentials: "include" });

  if (response.status === HTTP_UNAUTHORIZED && skipAutoLogout !== true) {
    // 401 = бэк отверг запрос. Реакцию на сессию выбирает код отказа
    // (docs/spec/core/routing.md § «Тело осознанного отказа»):
    // - `org_context_required` — сессия жива, нет активной организации:
    //   сбрасываем её, выбор организации сделает UI; не логаут;
    // - `not_authenticated` и 401 без объектного `detail` — сессия
    //   отвергнута: сбрасываем identity-флаг, `<ProtectedRoute>` на
    //   следующем рендере редиректнет на /login;
    // - 401 с иным кодом (отказ модуля) сессию не трогает.
    // Не дергаем navigate отсюда: SPA-роутинг живёт через React.
    const refusal = await readRefusal(response);
    if (refusal === null || refusal.code === REFUSAL_NOT_AUTHENTICATED) {
      useAuthStore.getState().logout();
    } else if (refusal.code === REFUSAL_ORG_CONTEXT_REQUIRED) {
      useAuthStore.getState().setActiveOrg(null);
    }
  }

  return response;
}
